<script>
  import { Link } from '@inertiajs/svelte'
  import Tooltip from '@components/general/tooltip.svelte'
  import TriggerChevron from '@components/general/trigger-chevron.svelte'
  import { Loader2 } from 'lucide-svelte'
  import { cn } from '@lib/utils'

  let {
    text,
    type = "button",
    disabled: disabledProp = false,
    loading = false,
    Icon,
    BeforeIcon,
    path,
    externalPath,
    target = "_blank",
    iconProps = {},
    hint,
    variant = "secondary",
    size = "md",
    children,
    class: className,
    style: styleProps,
    tooltipProps = {},
    viewTransition = false,
    open,
    ref = $bindable(),
    ...restProps
  } = $props()

  let disabled = $derived(disabledProp || loading)
  let actionable = $derived(Boolean(path || externalPath || restProps.onclick || type === "submit"))

  // let baseClasses = "text-smaller min-w-[2rem] bg-stone-100/80 hover:bg-stone-100 border border-stone-200/80 hover:border-stone-300/80 inline-flex gap-2 items-center text-gray-800 rounded hover:no-underline focus:outline-none focus:ring-2 focus:ring-blue-500/80"
  let baseClasses = "w-fit text-smaller min-w-[2rem] inline-flex gap-2 items-center text-gray-800 rounded hover:no-underline focus:outline-none focus:ring-2 focus:ring-blue-500/80"
  let nakedClasses = "p-0 text-inherit min-w-0"

  let variants = {
    primary: 'bg-primary-50 text-primary-700 border border-primary-500',
    secondary: 'bg-stone-100/80 hover:bg-stone-100 border border-stone-200/80',
    confirm: 'bg-green-50 text-green-600 border border-green-500',
    submit: 'bg-primary-50 text-primary-700 border border-primary-500',
    bold: 'bg-brand-blue/90 text-black font-semibold border border-black',
    warning: 'bg-red-50 text-red-700 border border-red-500',
    link: 'p-0 bg-transparent border-none text-gray-600 no-underline hover:bg-transparent hover:underline',
    outline: 'variant-outline bg-transparent text-gray-700 border border-gray-200 hover:bg-transparent hover:border-gray-200',
    naked: nakedClasses,
    menu_item: cn(nakedClasses, 'flex justify-start items-center gap-4 text-gray-600 text-smaller px-3 py-2 sm:px-4 sm:py-4 xl:py-5 cursor-pointer bg-transparent hover:bg-primary-50 hover:text-primary-900 relative focus:outline-none focus:ring-0'),
  }

  let variant_hover_classes = {
    primary: 'hover:bg-primary-100/80 hover:border-primary-600 hover:text-primary-800',
    secondary: 'hover:bg-primary-50 hover:text-primary-900',
    confirm: 'hover:bg-green-100/60 hover:text-green-700 hover:border-green-700',
    submit: 'hover:bg-primary-100/80 hover:border-primary-600 hover:text-primary-800',
    bold: 'hover:bg-brand-blue hover:border-black',
    warning: 'hover:bg-red-100/80 hover:text-red-800',
  }

  let sizeClasses = {
    xs: 'text-xs px-2 py-1 h-6',
    sm: 'text-smaller px-2 py-1 h-7',
    md: 'text-smaller px-3 py-[0.4rem] h-8',
    lg: 'text-sm px-4 py-2 h-9',
  }

  let classes = $derived(cn(
    baseClasses,
    variant ? variants[variant] : "",
    (variant && actionable && !["naked", "link"].includes(variant)) ? variant_hover_classes[variant] : "",
    ["link", "naked"].includes(variant) ? "" : sizeClasses[size],
    disabled ? "cursor-not-allowed opacity-60" : "",
    actionable && !disabled ? "cursor-pointer" : "",
    loading ? "loading" : "",
    className,
  ))

  let attributes = $derived({ class: classes, disabled, ...restProps })

  let style = $derived.by(() => {
    if (!styleProps) return null
    if (typeof styleProps === "string") return styleProps
    return Object.entries(styleProps).map(([key, value]) => `${key}: ${value}`).join(";")
  })

  let defaultIconProps = { size: 14, strokeWidth: 1.5, class: "text-inherit" }
  let derivedIconProps = $derived(Object.assign({}, defaultIconProps, iconProps))

  function mergeProps(tooltipProps, ourProps) {
    let merged = { ...tooltipProps, ...ourProps }
    for (let key of Object.keys(ourProps)) {
      if (key.startsWith('on') && typeof ourProps[key] === 'function') {
        const ours = ourProps[key]
        const theirs = tooltipProps[key]
        merged[key] = (e) => { ours(e); theirs?.(e) }
      }
    }
    return merged
  }

</script>

<!-- Lucide icons use strokeWidth, Tabler icons use stroke -->
{#snippet contents()}
  {#if BeforeIcon}
    <BeforeIcon class="text-inherit" {...derivedIconProps} />
  {/if}
  {text}
  {#if loading}
    <Loader2 class="animate-spin" {...derivedIconProps} />
  {:else if Icon}
    <Icon class="text-inherit" {...derivedIconProps} />
  {:else if (![null, undefined].includes(open))}
    <TriggerChevron {open} />
  {/if}
{/snippet}

{#snippet innerContents()}
  {#if children}
    {@render children()}
  {:else}
    {@render contents()}
  {/if}
{/snippet}

{#snippet buttonElement({ props } = {})}
  {@const merged = mergeProps(props ?? {}, externalPath ? { href: externalPath, target, style, ...attributes } : path ? { href: path, disabled, style, ...attributes } : { type, style, ...attributes })}
  {#if externalPath}
    <a {...merged}>{@render innerContents()}</a>
  {:else if path}
    <Link {viewTransition} {...merged}>{@render innerContents()}</Link>
  {:else}
    <button {...merged}>{@render innerContents()}</button>
  {/if}
{/snippet}

<Tooltip {hint} {...tooltipProps} triggerChild={buttonElement} children={buttonElement} />
